import JsBarcode from 'jsbarcode';

export type BarcodeSymbology = 'CODE128' | 'CODE39';

/** Bar height inside the 22 mm barcode panel (~half of 10 mm label). */
export const LABEL_BARCODE_SVG_MAX_HEIGHT_MM = 5;

/** Screen preview JsBarcode options. */
export const LABEL_BARCODE_PREVIEW_JS_OPTIONS = {
  width: 1.2,
  height: 36,
} as const;

/** Print JsBarcode → canvas (sharper through Windows Zebra GDI than rotated/SVG). */
export const LABEL_BARCODE_PRINT_JS_OPTIONS = {
  width: 2,
  height: 40,
  margin: 4,
} as const;

const BARCODE_ASPECT_RATIO = 'xMidYMid meet';

/**
 * Renders CODE128 (default) onto an SVG element. Value must match productCodeUnique for scanners.
 */
export function renderBarcodeSvg(
  svgEl: SVGSVGElement | null,
  value: string,
  options?: { format?: BarcodeSymbology; width?: number; height?: number },
): void {
  if (!svgEl || !value?.trim()) return;
  while (svgEl.firstChild) {
    svgEl.removeChild(svgEl.firstChild);
  }
  try {
    JsBarcode(svgEl, value, {
      format: options?.format ?? 'CODE128',
      width: options?.width ?? 0.85,
      height: options?.height ?? 28,
      displayValue: false,
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    });
    svgEl.setAttribute('preserveAspectRatio', BARCODE_ASPECT_RATIO);
  } catch {
    /* invalid data for symbology — leave svg empty */
  }
}

/** Inline SVG string (preview / fallback). */
export function barcodeToSvgString(
  value: string,
  options?: { format?: BarcodeSymbology; width?: number; height?: number },
): string {
  if (!value?.trim()) return '';
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  try {
    JsBarcode(svg, value, {
      format: options?.format ?? 'CODE128',
      width: options?.width ?? 0.75,
      height: options?.height ?? 26,
      displayValue: false,
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    });
  } catch {
    return '';
  }
  svg.setAttribute('preserveAspectRatio', BARCODE_ASPECT_RATIO);
  return svg.outerHTML;
}

/**
 * Raster CODE128 PNG for Chrome → ZDesigner. Avoids SVG print blur on thermal.
 */
export function barcodeToPngDataUrl(
  value: string,
  options?: { format?: BarcodeSymbology; width?: number; height?: number; margin?: number },
): string {
  if (!value?.trim()) return '';
  const canvas = document.createElement('canvas');
  try {
    JsBarcode(canvas, value, {
      format: options?.format ?? 'CODE128',
      width: options?.width ?? LABEL_BARCODE_PRINT_JS_OPTIONS.width,
      height: options?.height ?? LABEL_BARCODE_PRINT_JS_OPTIONS.height,
      displayValue: false,
      margin: options?.margin ?? LABEL_BARCODE_PRINT_JS_OPTIONS.margin,
      background: '#ffffff',
      lineColor: '#000000',
    });
  } catch {
    return '';
  }
  return canvas.toDataURL('image/png');
}
